// client/src/pages/Admin/CourseEditor/index.jsx
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
// import customFetch from "../../../api/customFetch";
import customFetch from "../../api/customFetch.js";
import QuestionEditor from "./QuestionEditor.jsx";
import { toast } from "react-toastify";

// רשימת הסקטורים
const SECTORS = [
  "אחמש",
  "גבסן",
  "הנהלה",
  "טכאי אקו",
  "טכנאי רנטגן",
  "מאמנים",
  "מזכירות",
  "מעבדה",
  "רופאים",
  "סיעוד",
];

export default function CourseEditor() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [course, setCourse] = useState({
    slug: "",
    title: "",
    passScore: 80,
    videoUrl: "",
    slides: [],
    questions: [],
    isPublished: false,
    targetSectors: [],
    defaultAnswers: {
      positive: "הטרדה",
      negative: "לא הטרדה",
    },
    supervisorEmail: "", // חדש
    daysToComplete: null,
    sendOnCompletion: true,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState("details"); // details, slides, questions

  const isNew = slug === "new";

  useEffect(() => {
    if (isNew) return;
    if (!slug || slug === "undefined") return;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const { data } = await customFetch.get(`/courses/admin/${slug}`);

        if (data.success && data.course) {
          setCourse({
            ...data.course,
            targetSectors: data.course.targetSectors || [],
            defaultAnswers: data.course.defaultAnswers || {
              positive: "הטרדה",
              negative: "לא הטרדה",
            },
          });
        } else if (data.slug) {
          setCourse({
            ...data,
            targetSectors: data.targetSectors || [],
            defaultAnswers: data.defaultAnswers || {
              positive: "הטרדה",
              negative: "לא הטרדה",
            },
          });
        } else {
          setError("מבנה נתונים לא תקין");
        }
      } catch (e) {
        if (e.response?.status === 401 || e.response?.status === 403) {
          nav("/admin/login");
        } else {
          setError("שגיאה בטעינת הקורס");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, isNew, nav]);

  const save = async () => {
    if (!course.slug) {
      setError("נדרש נתיב לקורס");
      return;
    }
    if (!course.title) {
      setError("נדרש שם לקורס");
      return;
    }
    if (course.targetSectors.length === 0) {
      setError("נדרש לבחור לפחות סקטור אחד");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const { data } = await customFetch.post("/courses/admin/upsert", course);

      if (data.success && data.course) {
        setCourse(data.course);
        setSuccess("הלומדה נשמרה בהצלחה!");
        if (isNew) nav(`/admin/courses/${data.course.slug}`);
      } else if (data.slug) {
        setCourse(data);
        setSuccess("הלומדה נשמרה בהצלחה!");
        if (isNew) nav(`/admin/courses/${data.slug}`);
      } else {
        setError("שגיאה בשמירה - מבנה תשובה לא תקין");
      }
    } catch (e) {
      setError(`שגיאה בשמירת הקורס: ${e.response?.data?.message || e.message}`);
    } finally {
      setSaving(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const publish = async () => {
    try {
      setSaving(true);
      const { data } = await customFetch.patch(
        `/courses/admin/${course.slug}/publish`
      );

      if (data.success && data.course) {
        setCourse(data.course);
      } else {
        setCourse((c) => ({ ...c, isPublished: true }));
      }
      setSuccess("הלומדה פורסמה בהצלחה!");
    } catch (e) {
      setError("שגיאה בפרסום הקורס");
    } finally {
      setSaving(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const unpublish = async () => {
    try {
      setSaving(true);
      const { data } = await customFetch.patch(
        `/courses/admin/${course.slug}/unpublish`
      );

      if (data.success && data.course) {
        setCourse(data.course);
      } else {
        setCourse((c) => ({ ...c, isPublished: false }));
      }
      setSuccess("הלומדה הוסרה מפרסום");
    } catch (e) {
      setError("שגיאה בביטול פרסום הקורס");
    } finally {
      setSaving(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const importJson = async (file) => {
    try {
      const txt = await file.text();
      const obj = JSON.parse(txt);
      setCourse({
        ...obj,
        targetSectors: obj.targetSectors || [],
        defaultAnswers: obj.defaultAnswers || {
          positive: "הטרדה",
          negative: "לא הטרדה",
        },
      });
      setSuccess("הקובץ יובא בהצלחה!");
      setError(null);
    } catch (e) {
      setError("שגיאה בייבוא קובץ JSON");
    }
  };

  const toggleSector = (sector) => {
    setCourse((prev) => ({
      ...prev,
      targetSectors: prev.targetSectors.includes(sector)
        ? prev.targetSectors.filter((s) => s !== sector)
        : [...prev.targetSectors, sector],
    }));
  };

  const selectAllSectors = () => {
    setCourse((prev) => ({ ...prev, targetSectors: [...SECTORS] }));
  };

  const clearAllSectors = () => {
    setCourse((prev) => ({ ...prev, targetSectors: [] }));
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span>טוען לומדה...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {/* Page Header */}
      <div className="page-header">
        <h1 className="page-title">
          <span className="page-title-icon">{isNew ? "➕" : "✏️"}</span>
          {isNew ? "יצירת לומדה חדשה" : course.title || "עריכת לומדה"}
          {!isNew && (
            <span
              className={`badge ${course.isPublished ? "success" : "warning"}`}
              style={{ marginRight: "12px" }}
            >
              {course.isPublished ? "🟢 מפורסם" : "⚪ טיוטה"}
            </span>
          )}
        </h1>
        <div className="page-actions">
          <button
            className="admin-btn secondary"
            onClick={() => nav("/admin/courses")}
          >
            ← חזור לרשימה
          </button>
          {!isNew && course.slug && (
            <button
              className="admin-btn secondary"
              onClick={() => window.open(`/${course.slug}`, "_blank")}
            >
              👁️ צפייה מקדימה
            </button>
          )}
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="admin-alert error">
          <span>⚠️</span>
          <span>{error}</span>
          <button className="alert-close" onClick={() => setError(null)}>
            ✕
          </button>
        </div>
      )}

      {success && (
        <div className="admin-alert success">
          <span>✅</span>
          <span>{success}</span>
          <button className="alert-close" onClick={() => setSuccess(null)}>
            ✕
          </button>
        </div>
      )}

      {/* הגדרות התראות */}
      <div
        style={{
          marginTop: "24px",
          paddingTop: "24px",
          borderTop: "1px solid var(--admin-border)",
        }}
      >
        <h4 style={{ color: "var(--admin-text)", marginBottom: "16px" }}>
          📧 הגדרות התראות
        </h4>

        <div className="form-row">
          <div className="form-group">
            <label className="admin-label">מייל מנהל לקבלת דיווחים</label>
            <input
              className="admin-input"
              type="email"
              value={course.supervisorEmail || ""}
              onChange={(e) =>
                setCourse({ ...course, supervisorEmail: e.target.value })
              }
              placeholder="manager@company.com"
            />
          </div>

          <div className="form-group">
            <label className="admin-label">ימים להשלמה</label>
            <input
              className="admin-input"
              type="number"
              min="1"
              value={course.daysToComplete || ""}
              onChange={(e) =>
                setCourse({
                  ...course,
                  daysToComplete: e.target.value ? +e.target.value : null,
                })
              }
              placeholder="ללא הגבלה"
            />
          </div>
        </div>

        {/* אפשרויות שליחה */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            // marginTop: "16px",
            padding: "16px",
            background: "var(--admin-bg)",
            borderRadius: "8px",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              cursor: "pointer",
              color: "var(--admin-text)",
            }}
          >
            <input
              type="checkbox"
              checked={course.sendOnCompletion ?? true}
              onChange={(e) =>
                setCourse({ ...course, sendOnCompletion: e.target.checked })
              }
              style={{
                width: "18px",
                height: "18px",
                accentColor: "var(--admin-primary)",
              }}
            />
            <span>שלח מייל למנהל בכל סיום לומדה</span>
          </label>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              cursor: course.daysToComplete ? "pointer" : "not-allowed",
              color: course.daysToComplete
                ? "var(--admin-text)"
                : "var(--admin-text-muted)",
              opacity: course.daysToComplete ? 1 : 0.5,
            }}
          >
            <input
              type="checkbox"
              checked={true}
              disabled={true}
              style={{ width: "18px", height: "18px" }}
            />
            <span>שלח רשימת מפגרים (אוטומטי כשיש ימים להשלמה)</span>
          </label>
        </div>

        <small
          style={{
            color: "var(--admin-text-muted)",
            fontSize: "0.8rem",
            marginTop: "8px",
            display: "block",
          }}
        >
          💡 רשימת המפגרים תישלח אוטומטית למייל המנהל כשמוגדרים ימים להשלמה
        </small>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          marginBottom: "24px",
          borderBottom: "1px solid var(--admin-border)",
          paddingBottom: "8px",
        }}
      >
        {[
          { id: "details", label: "📋 פרטים", count: null },
          {
            id: "slides",
            label: "🖼️ שקפים",
            count: course.slides?.length || 0,
          },
          {
            id: "questions",
            label: "❓ שאלות",
            count: course.questions?.length || 0,
          },
        ].map((tab) => (
          <button
            key={tab.id}
            className={`admin-btn ${
              activeTab === tab.id ? "primary" : "secondary"
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {tab.count !== null && (
              <span className="badge info" style={{ marginRight: "8px" }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "details" && (
        <div className="admin-card">
          <h3 className="card-title" style={{ marginBottom: "24px" }}>
            פרטי הלומדה
          </h3>

          <div className="form-row">
            <div className="form-group">
              <label className="admin-label required">נתיב הלומדה (slug)</label>
              <input
                className="admin-input"
                value={course.slug}
                onChange={(e) => setCourse({ ...course, slug: e.target.value })}
                placeholder="harassment-101"
                disabled={!isNew}
              />
            </div>

            <div className="form-group">
              <label className="admin-label required">כותרת</label>
              <input
                className="admin-input"
                value={course.title}
                onChange={(e) =>
                  setCourse({ ...course, title: e.target.value })
                }
                placeholder="שם הלומדה"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="admin-label">ציון עובר (%)</label>
              <input
                className="admin-input"
                type="number"
                min="0"
                max="100"
                value={course.passScore}
                onChange={(e) =>
                  setCourse({ ...course, passScore: +e.target.value })
                }
              />
            </div>

            <div className="form-group">
              <label className="admin-label">וידאו פתיחה (URL)</label>
              <input
                className="admin-input"
                value={course.videoUrl}
                onChange={(e) =>
                  setCourse({ ...course, videoUrl: e.target.value })
                }
                placeholder="https://..."
              />
            </div>
          </div>

          {/* Sectors Selection */}
          <div className="form-group">
            <label className="admin-label required">סקטורים מיועדים</label>
            <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
              <button
                type="button"
                className="admin-btn secondary sm"
                onClick={selectAllSectors}
              >
                בחר הכל
              </button>
              <button
                type="button"
                className="admin-btn secondary sm"
                onClick={clearAllSectors}
              >
                בטל הכל
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: "8px",
              }}
            >
              {SECTORS.map((sector) => (
                <label
                  key={sector}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    background: course.targetSectors.includes(sector)
                      ? "rgba(34, 197, 94, 0.15)"
                      : "var(--admin-bg)",
                    border: course.targetSectors.includes(sector)
                      ? "1px solid var(--admin-success)"
                      : "1px solid var(--admin-border)",
                    transition: "all 0.2s",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={course.targetSectors.includes(sector)}
                    onChange={() => toggleSector(sector)}
                    style={{ accentColor: "var(--admin-success)" }}
                  />
                  <span
                    style={{
                      color: course.targetSectors.includes(sector)
                        ? "var(--admin-success)"
                        : "var(--admin-text)",
                    }}
                  >
                    {sector}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Import JSON */}
          <div
            className="form-group"
            style={{
              marginTop: "24px",
              paddingTop: "24px",
              borderTop: "1px solid var(--admin-border)",
            }}
          >
            <label className="admin-label">ייבוא מקובץ JSON</label>
            <input
              type="file"
              accept="application/json"
              onChange={(e) =>
                e.target.files[0] && importJson(e.target.files[0])
              }
              style={{
                padding: "12px",
                background: "var(--admin-bg)",
                border: "1px dashed var(--admin-border)",
                borderRadius: "8px",
                color: "var(--admin-text)",
                width: "100%",
              }}
            />
          </div>
        </div>
      )}

      {activeTab === "slides" && (
        <div>
          {(course.slides || []).length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🖼️</div>
              <h3>אין שקפים</h3>
              <p>הוסף שקפים ללומדה</p>
            </div>
          ) : (
            <div className="data-grid">
              {(course.slides || []).map((s, idx) => (
                <div key={idx} className="admin-card">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "16px",
                    }}
                  >
                    <h4 style={{ margin: 0, color: "var(--admin-text)" }}>
                      שקף {idx + 1}
                    </h4>
                    <button
                      className="admin-btn danger sm"
                      onClick={() => {
                        const slides = course.slides.filter(
                          (_, i) => i !== idx
                        );
                        setCourse({ ...course, slides });
                      }}
                    >
                      🗑️ מחק
                    </button>
                  </div>

                  <div className="form-group">
                    <label className="admin-label">כותרת</label>
                    <input
                      className="admin-input"
                      placeholder="כותרת השקף"
                      value={s.title || ""}
                      onChange={(e) => {
                        const slides = [...course.slides];
                        slides[idx] = { ...s, title: e.target.value };
                        setCourse({ ...course, slides });
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label className="admin-label">טקסט</label>
                    <textarea
                      className="admin-textarea"
                      placeholder="תוכן השקף"
                      rows={3}
                      value={s.text || ""}
                      onChange={(e) => {
                        const slides = [...course.slides];
                        slides[idx] = { ...s, text: e.target.value };
                        setCourse({ ...course, slides });
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label className="admin-label">תמונה (URL)</label>
                    <input
                      className="admin-input"
                      placeholder="https://..."
                      value={s.imageUrl || ""}
                      onChange={(e) => {
                        const slides = [...course.slides];
                        slides[idx] = { ...s, imageUrl: e.target.value };
                        setCourse({ ...course, slides });
                      }}
                    />
                  </div>

                  {s.imageUrl && (
                    <div style={{ marginTop: "12px" }}>
                      <img
                        src={s.imageUrl}
                        alt="תצוגה מקדימה"
                        style={{
                          maxWidth: "200px",
                          maxHeight: "150px",
                          borderRadius: "8px",
                          border: "1px solid var(--admin-border)",
                        }}
                        onError={(e) => (e.target.style.display = "none")}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <button
            className="admin-btn primary"
            style={{ marginTop: "16px" }}
            onClick={() =>
              setCourse({
                ...course,
                slides: [
                  ...(course.slides || []),
                  { title: "", text: "", imageUrl: "" },
                ],
              })
            }
          >
            ➕ הוסף שקף
          </button>
        </div>
      )}

      {activeTab === "questions" && (
        <div>
          {(course.questions || []).length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">❓</div>
              <h3>אין שאלות</h3>
              <p>הוסף שאלות למבחן הלומדה</p>
            </div>
          ) : (
            <div className="data-grid">
              {(course.questions || []).map((q, idx) => (
                <QuestionEditor
                  key={idx}
                  value={q}
                  index={idx}
                  onChange={(next) => {
                    const questions = [...course.questions];
                    questions[idx] = next;
                    setCourse({ ...course, questions });
                  }}
                  onRemove={() => {
                    const questions = course.questions.filter(
                      (_, i) => i !== idx
                    );
                    setCourse({ ...course, questions });
                  }}
                />
              ))}
            </div>
          )}

          <button
            className="admin-btn primary"
            style={{ marginTop: "16px" }}
            onClick={() =>
              setCourse({
                ...course,
                questions: [
                  ...(course.questions || []),
                  {
                    prompt: "",
                    imageUrl: "",
                    answers: [
                      {
                        label: course.defaultAnswers.positive,
                        isCorrect: true,
                        explain: "",
                      },
                      {
                        label: course.defaultAnswers.negative,
                        isCorrect: false,
                        explain: "",
                      },
                    ],
                  },
                ],
              })
            }
          >
            ➕ הוסף שאלה ({course.defaultAnswers.positive} /{" "}
            {course.defaultAnswers.negative})
          </button>
        </div>
      )}

      {/* Action Bar */}
      <div
        className="admin-card"
        style={{
          marginTop: "24px",
          position: "sticky",
          bottom: "24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "var(--admin-sidebar)",
          borderColor: "var(--admin-primary)",
        }}
      >
        <div style={{ display: "flex", gap: "12px" }}>
          <button
            className="admin-btn primary"
            onClick={save}
            disabled={saving}
          >
            {saving ? "⏳ שומר..." : "💾 שמור שינויים"}
          </button>

          {course.slug &&
            !isNew &&
            (course.isPublished ? (
              <button
                className="admin-btn warning"
                onClick={unpublish}
                disabled={saving}
              >
                📤 הסר מפרסום
              </button>
            ) : (
              <button
                className="admin-btn success"
                onClick={publish}
                disabled={saving}
              >
                📥 פרסם לומדה
              </button>
            ))}
        </div>

        <div style={{ color: "var(--admin-text-muted)", fontSize: "0.85rem" }}>
          {course.slides?.length || 0} שקפים | {course.questions?.length || 0}{" "}
          שאלות | {course.targetSectors?.length || 0} סקטורים
        </div>
      </div>
    </div>
  );
}
